import React, { useState } from "react";
import {
	Button,
	Card,
	Grid,
	Typography,
	CardContent,
	CardActions,
	Box,
	Icon
} from "@material-ui/core";
import { ImageFit } from "@ploy-ui/core";
import { useTenantImage } from "@ploy-ui/tenants";
import { Theme } from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import clsx from "clsx";
import { FormattedMessage } from "react-intl";
import CircularProgress from "@material-ui/core/CircularProgress";

interface RequestCookieAccessProps {
	callback: (hasCookies: boolean) => void;
}

declare global {
	interface Document {
		hasStorageAccess: () => Promise<boolean>;
		requestStorageAccess: () => Promise<void>;
	}
}

const requestStorageFromBrowser = async (
	setAccessFailed: (access: boolean) => void,
	setCookieAccessProcess: (process: 0 | 1 | 2) => void,
	callback: (hasCookies: boolean) => any
) => {
	var requestStorage = document.requestStorageAccess();

	requestStorage.then(
		function() {
			if (
				!document.cookie.match(
					/^(.*;)?\s*checkCookie\s*=\s*[^;]+(.*)?$/ //https://stackoverflow.com/a/25617724
				)
			) {
				//we have access but no cookie is set
				setAccessFailed(true);
				window.open("/firstpartyaccess?imediateReturn=true", "_blank"); //just need cookie, imediate return

				setTimeout(() => {
					callback(true);
					//console.log("Got storage access");
				}, 1000); //should have cookie by now
			} else {
				//we have access and cookie
				callback(true);
				//console.log("Got storage access");
			}
		},
		function() {
			//we do not have access to cookies/storage, in order for us to get it, user must have an interaction on our domain
			//console.log("ERROR: unable to get storage access.");
			setAccessFailed(true);
			setCookieAccessProcess(1);
			const popup = window.open("/firstpartyaccess", "_blank"); //user can set cookie on button and thereby get interaction as well
			popup?.addEventListener("unload", () => {
				if (popup.closed) {
					setCookieAccessProcess(2);
				}
			});
		}
	);
};

const useStyles = makeStyles(
	(theme: Theme) => ({
		root: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			flexDirection: "column"
		},
		logoWrapper: {
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(2)
		},
		logo: {
			width: "100%",
			height: 64
		},
		cardAction: {
			justifyContent: "center"
		}
	}),
	{ name: "DployRequestCookieAccess" }
);

export const RequestCookieAccess = (props: RequestCookieAccessProps) => {
	const { callback } = props;
	const classes = useStyles(props);

	const [accessFailed, setAccessFailed] = useState(false);
	const [cookieAccessProcess, setCookieAccessProcess] = useState<0 | 1 | 2>(0);
	const logo = useTenantImage("form_web_logo");

	return (
		<Box style={{ padding: 20 }}>
			<Grid container spacing={4} alignItems="center" justify="center">
				<Grid item xs={12} md={8} lg={6}>
					<Card title="Allow access">
						<CardContent>
							<div className={clsx(classes.logoWrapper, classes.root)}>
								{logo ? (
									<ImageFit
										className={classes.logo}
										src={logo}
										alt="logo"
										fit="contain"
									/>
								) : (
									<Icon
										className="fa fa-cookie-bite"
										style={{ fontSize: 30 }}
									/>
								)}
							</div>
							<Typography>
								<FormattedMessage
									id="requestCookieAccess.card.content.infotext"
									defaultMessage="Denne nettleseren trenger at du gir den tillatelse til å kommunisere med vårt nettsted for at søknadsskjemaet skal fungere.
									Ved trykk på knappen åpner vi et nytt vindu hvor vi vil be deg om å trykke på en knapp."
								/>
							</Typography>
						</CardContent>
						<CardActions classes={{ root: classes.cardAction }}>
							<Button
								variant={"contained"}
								color="secondary"
								onClick={() =>
									requestStorageFromBrowser(
										setAccessFailed,
										setCookieAccessProcess,
										callback
									)
								}
							>
								{cookieAccessProcess !== 2 ? (
									<FormattedMessage
										id="requestCookieAccess.card.content.allowAccessButton"
										defaultMessage="Gi tilgang"
									/>
								) : (
									<FormattedMessage
										id="requestCookieAccess.card.content.confirmAccessButton"
										defaultMessage="Bekreft tilgang"
									/>
								)}
							</Button>
						</CardActions>
						{accessFailed && (
							<CardContent>
								<Typography>
									{cookieAccessProcess === 0 && (
										<FormattedMessage
											id="requestCookieAccess.card.content.accessFailedText"
											defaultMessage="Fikk ikke tilgang, vennligst prøv igjen."
										/>
									)}
									{cookieAccessProcess === 1 && (
										<>
											<FormattedMessage
												id="requestCookieAccess.card.content.waitingForConsent"
												defaultMessage="Følg instruksene i vinduet som åpnet seg."
											/>
											<CircularProgress size="1em" />
										</>
									)}
									{cookieAccessProcess === 2 && (
										<FormattedMessage
											id="requestCookieAccess.card.content.consentGiven"
											defaultMessage="Du har nå forhåpentligvis gitt ditt svar. Bekreft tilgangen ved å trykke på knappen over."
										/>
									)}
								</Typography>
							</CardContent>
						)}
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
};

RequestCookieAccess.displayName = "DployRequestCookieAccess";
